import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, CheckCircle, GraduationCap, Shield, Clock, Users, Monitor, Mail, CalendarIcon, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { SEOHead } from '@/components/SEOHead';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format, getDay, isAfter, startOfDay, addMonths } from 'date-fns';
import { fr as frLocale } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { z } from 'zod';
import { WaitlistDialog } from '@/components/training/WaitlistDialog';

type Step = 'info' | 'account' | 'slot' | 'payment' | 'confirmation';

const guestSchema = z.object({
  fullName: z.string().trim().min(2, 'Minimum 2 caractères').max(100),
  email: z.string().trim().email('Email invalide').max(255),
});

const CRFPA_PRICE = '199,00 € HT';
const PAYMENT_LINK_PLACEHOLDER = '#'; // TODO: Replace with actual payment link

const CALENDAR_START = new Date(2026, 4, 1); // May 1, 2026

// Only Tuesdays (2) and Thursdays (4) are available from May 2026
const isAvailableDay = (date: Date) => {
  const day = getDay(date);
  return (day === 2 || day === 4) && isAfter(date, startOfDay(CALENDAR_START));
};

export const CRFPABookingPage = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isFr = language === 'fr';

  const [step, setStep] = useState<Step>('info');
  const [acceptCGU, setAcceptCGU] = useState(false);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestErrors, setGuestErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvc, setCardCvc] = useState('');
  const [showWaitlist, setShowWaitlist] = useState(false);

  const allSteps: { key: Step; label: string }[] = [
    { key: 'info', label: isFr ? 'Récapitulatif' : 'Summary' },
    { key: 'account', label: isFr ? 'Compte' : 'Account' },
    { key: 'slot', label: isFr ? 'Créneau' : 'Session' },
    { key: 'payment', label: isFr ? 'Paiement' : 'Payment' },
    { key: 'confirmation', label: isFr ? 'Confirmation' : 'Confirmation' },
  ];

  const currentStepIndex = allSteps.findIndex(s => s.key === step);

  const calendarRange = useMemo(() => ({
    from: new Date(),
    to: new Date(2026, 11, 31),
  }), []);

  useEffect(() => {
    if (step === 'payment') setShowWaitlist(true);
  }, [step]);

  const handleContinueToAccount = () => {
    if (!acceptCGU) {
      toast({
        variant: 'destructive',
        title: isFr ? 'CGU requises' : 'Terms required',
        description: isFr ? 'Veuillez accepter les conditions générales d\'utilisation.' : 'Please accept the terms of service.',
      });
      return;
    }
    if (user) {
      setStep('slot');
    } else {
      setStep('account');
    }
  };

  const handleGuestContinue = () => {
    setGuestErrors({});
    try {
      guestSchema.parse({ fullName: guestName, email: guestEmail });
      setStep('slot');
    } catch (err) {
      if (err instanceof z.ZodError) {
        const errors: Record<string, string> = {};
        err.errors.forEach(e => { if (e.path[0]) errors[e.path[0] as string] = e.message; });
        setGuestErrors(errors);
      }
    }
  };

  const handleSlotContinue = () => {
    if (!selectedDate) {
      toast({
        variant: 'destructive',
        title: isFr ? 'Créneau requis' : 'Session required',
        description: isFr ? 'Veuillez sélectionner un créneau.' : 'Please select a session.',
      });
      return;
    }
    setStep('payment');
  };

  const handlePayment = async () => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase.rpc('insert_enrollment', {
        p_training_slug: 'crfpa-ia',
        p_training_title: isFr ? 'Formation IA spéciale CRFPA' : 'Special CRFPA AI Training',
        p_modality: 'distanciel',
        p_total_price: CRFPA_PRICE,
        p_guest_name: user ? null : guestName || null,
        p_guest_email: user ? null : guestEmail || null,
        p_selected_webinars: selectedDate ? JSON.stringify({ date: format(selectedDate, 'yyyy-MM-dd'), time: '12:15-14:15' }) : null,
      });

      if (error) {
        toast({ variant: 'destructive', title: isFr ? 'Erreur' : 'Error', description: error.message });
        setIsSubmitting(false);
        return;
      }

      setStep('confirmation');
    } catch {
      toast({ variant: 'destructive', title: isFr ? 'Erreur' : 'Error', description: isFr ? 'Une erreur est survenue.' : 'An error occurred.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formattedSelectedDate = selectedDate
    ? isFr
      ? format(selectedDate, "EEEE d MMMM yyyy", { locale: frLocale })
      : format(selectedDate, "EEEE, MMMM d, yyyy")
    : null;

  return (
    <div className="min-h-screen bg-secondary/30">
      <SEOHead
        title={isFr ? 'Réservation Formation IA CRFPA | Gaius' : 'Book CRFPA AI Training | Gaius'}
        description={isFr ? 'Réservez votre session de formation IA spéciale CRFPA - 199€ HT par participant' : 'Book your special CRFPA AI training session - €199 excl. VAT per participant'}
        canonicalUrl={isFr ? 'https://gaius-tech.com/reservation-formation-crfpa' : 'https://gaius-tech.com/book-crfpa-ai-training'}
        alternateUrlFr="https://gaius-tech.com/reservation-formation-crfpa"
        alternateUrlEn="https://gaius-tech.com/book-crfpa-ai-training"
      />

      <div className="max-w-3xl mx-auto px-4 py-8 sm:py-12">
        {/* Progress bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {allSteps.map((s, i) => (
              <div key={s.key} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  i < currentStepIndex ? 'bg-primary text-primary-foreground' :
                  i === currentStepIndex ? 'bg-primary text-primary-foreground ring-4 ring-primary/20' :
                  'bg-muted text-muted-foreground'
                }`}>
                  {i < currentStepIndex ? <CheckCircle className="w-4 h-4" /> : i + 1}
                </div>
                {i < allSteps.length - 1 && (
                  <div className={`hidden sm:block w-12 lg:w-20 h-0.5 mx-1 ${i < currentStepIndex ? 'bg-primary' : 'bg-muted'}`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between">
            {allSteps.map(s => (
              <span key={s.key} className="text-xs text-muted-foreground hidden sm:block">{s.label}</span>
            ))}
          </div>
        </div>

        {/* Step 1: Info & CGU */}
        {step === 'info' && (
          <Card className="border-border shadow-lg">
            <CardHeader>
              <div className="flex items-center gap-2 mb-2">
                <GraduationCap className="w-6 h-6 text-primary" />
                <Badge className="bg-primary/10 text-primary border-primary/20">CRFPA</Badge>
              </div>
              <CardTitle className="text-xl">
                {isFr ? 'Formation IA spéciale CRFPA' : 'Special CRFPA AI Training'}
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                {isFr ? 'L\'IA au service de votre préparation au barreau' : 'AI at the service of your bar exam preparation'}
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 rounded-xl border border-border bg-card space-y-3">
                <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-primary" /> {isFr ? '2 heures' : '2 hours'}</span>
                  <span className="flex items-center gap-1.5"><Users className="w-4 h-4 text-primary" /> {isFr ? '20 participants max' : '20 participants max'}</span>
                  <span className="flex items-center gap-1.5"><Monitor className="w-4 h-4 text-primary" /> {isFr ? 'Visio en direct' : 'Live video conference'}</span>
                </div>
                <div className="border-t border-border pt-3 flex justify-between items-center">
                  <span className="text-muted-foreground">{isFr ? 'Tarif par participant' : 'Price per participant'}</span>
                  <span className="text-2xl font-bold text-foreground">{CRFPA_PRICE}</span>
                </div>
              </div>

              <div className="space-y-2">
                {[
                  isFr ? 'Maîtriser ChatGPT, Claude et Gemini pour structurer ses révisions' : 'Master ChatGPT, Claude and Gemini for structured revision',
                  isFr ? 'Identifier et vérifier les hallucinations de l\'IA' : 'Identify and verify AI hallucinations',
                  isFr ? 'Respecter les principes déontologiques' : 'Respect ethical principles',
                ].map((point, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground">{point}</span>
                  </div>
                ))}
              </div>

              <div className="p-4 rounded-xl border border-border bg-muted/30">
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox checked={acceptCGU} onCheckedChange={(v) => setAcceptCGU(v === true)} className="mt-0.5" />
                  <span className="text-sm text-muted-foreground">
                    {isFr ? (
                      <>J'accepte les <Link to="/conditions-utilisation" className="text-primary underline hover:no-underline">conditions générales d'utilisation</Link> et la <Link to="/confidentialite" className="text-primary underline hover:no-underline">politique de confidentialité</Link> de Gaius.</>
                    ) : (
                      <>I accept the <Link to="/terms-of-service" className="text-primary underline hover:no-underline">terms of service</Link> and <Link to="/privacy-policy" className="text-primary underline hover:no-underline">privacy policy</Link>.</>
                    )}
                  </span>
                </label>
              </div>

              <div className="flex justify-between pt-2">
                <Button variant="outline" asChild>
                  <Link to={isFr ? '/formation-ia-avocat' : '/legal-ai-training-lawyers'}>
                    <ArrowLeft className="w-4 h-4 mr-2" />{isFr ? 'Retour' : 'Back'}
                  </Link>
                </Button>
                <Button onClick={handleContinueToAccount} disabled={!acceptCGU}>
                  {isFr ? 'Continuer' : 'Continue'}<ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Account */}
        {step === 'account' && (
          <Card className="border-border shadow-lg">
            <CardHeader>
              <CardTitle className="text-xl">{isFr ? 'Votre compte' : 'Your account'}</CardTitle>
              <p className="text-sm text-muted-foreground">
                {isFr ? 'Créez un compte pour suivre vos formations, ou continuez en tant qu\'invité.' : 'Create an account to track your trainings, or continue as guest.'}
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Button asChild variant="outline" className="h-auto p-4 flex flex-col items-start gap-2">
                  <Link to={isFr ? '/connexion' : '/auth'}>
                    <Shield className="w-5 h-5 text-primary" />
                    <span className="font-semibold">{isFr ? 'Créer un compte' : 'Create account'}</span>
                    <span className="text-xs text-muted-foreground text-left">{isFr ? 'Accédez à vos formations et ressources' : 'Access your trainings and resources'}</span>
                  </Link>
                </Button>
                <Button asChild variant="outline" className="h-auto p-4 flex flex-col items-start gap-2">
                  <Link to={isFr ? '/connexion' : '/auth'}>
                    <Mail className="w-5 h-5 text-primary" />
                    <span className="font-semibold">{isFr ? 'Se connecter' : 'Sign in'}</span>
                    <span className="text-xs text-muted-foreground text-left">{isFr ? 'Déjà un compte Gaius' : 'Already have a Gaius account'}</span>
                  </Link>
                </Button>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
                <div className="relative flex justify-center text-xs uppercase"><span className="bg-card px-2 text-muted-foreground">{isFr ? 'ou' : 'or'}</span></div>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-foreground">{isFr ? 'Continuer en tant qu\'invité' : 'Continue as guest'}</h3>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="guest-name">{isFr ? 'Nom complet' : 'Full name'}</Label>
                    <Input id="guest-name" value={guestName} onChange={e => setGuestName(e.target.value)} placeholder={isFr ? 'Jean Dupont' : 'John Doe'} />
                    {guestErrors.fullName && <p className="text-xs text-destructive mt-1">{guestErrors.fullName}</p>}
                  </div>
                  <div>
                    <Label htmlFor="guest-email">Email</Label>
                    <Input id="guest-email" type="email" value={guestEmail} onChange={e => setGuestEmail(e.target.value)} placeholder="email@cabinet.com" />
                    {guestErrors.email && <p className="text-xs text-destructive mt-1">{guestErrors.email}</p>}
                  </div>
                </div>
                <Button onClick={handleGuestContinue} className="w-full">
                  {isFr ? 'Continuer en invité' : 'Continue as guest'}<ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>

              <div className="flex justify-start pt-2">
                <Button variant="outline" onClick={() => setStep('info')}>
                  <ArrowLeft className="w-4 h-4 mr-2" />{isFr ? 'Retour' : 'Back'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Slot selection */}
        {step === 'slot' && (
          <Card className="border-border shadow-lg">
            <CardHeader>
              <CardTitle className="text-xl flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-primary" />
                {isFr ? 'Choisissez votre créneau' : 'Choose your session'}
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                {isFr
                  ? 'Sessions disponibles les mardis et jeudis, de 12h15 à 14h15 (visioconférence en direct).'
                  : 'Sessions available on Tuesdays and Thursdays, 12:15 PM – 2:15 PM (live video conference).'}
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Schedule info badges */}
              <div className="flex flex-wrap gap-3">
                <Badge variant="outline" className="text-sm px-3 py-1.5">
                  <CalendarIcon className="w-3.5 h-3.5 mr-1.5" />
                  {isFr ? 'Mardi & Jeudi' : 'Tuesday & Thursday'}
                </Badge>
                <Badge variant="outline" className="text-sm px-3 py-1.5">
                  <Clock className="w-3.5 h-3.5 mr-1.5" />
                  12:15 – 14:15
                </Badge>
                <Badge variant="outline" className="text-sm px-3 py-1.5">
                  <Monitor className="w-3.5 h-3.5 mr-1.5" />
                  {isFr ? 'Visioconférence' : 'Video conference'}
                </Badge>
              </div>

              {/* Calendar */}
              <div className="flex justify-center">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={(date) => !isAvailableDay(date)}
                  fromDate={CALENDAR_START}
                  toDate={calendarRange.to}
                  locale={isFr ? frLocale : undefined}
                  className={cn("p-3 pointer-events-auto rounded-xl border border-border")}
                  initialFocus
                />
              </div>

              {/* Selected date display */}
              {selectedDate && (
                <div className="p-4 rounded-xl bg-primary/5 border border-primary/20">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground capitalize">{formattedSelectedDate}</p>
                      <p className="text-sm text-muted-foreground">12:15 – 14:15</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-between pt-2">
                <Button variant="outline" onClick={() => user ? setStep('info') : setStep('account')}>
                  <ArrowLeft className="w-4 h-4 mr-2" />{isFr ? 'Retour' : 'Back'}
                </Button>
                <Button onClick={handleSlotContinue} disabled={!selectedDate}>
                  {isFr ? 'Continuer' : 'Continue'}<ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Payment */}
        {step === 'payment' && (
          <Card className="border-border shadow-lg">
            <CardHeader>
              <CardTitle className="text-xl flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-primary" />
                {isFr ? 'Paiement par carte bancaire' : 'Card payment'}
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                {isFr ? 'Paiement sécurisé — Intégration Stripe à venir' : 'Secure payment — Stripe integration coming soon'}
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-3 rounded-lg bg-accent/10 border border-accent/20 text-sm text-accent-foreground">
                {isFr ? '⚠️ Mode simulation : aucun paiement réel ne sera effectué.' : '⚠️ Simulation mode: no real payment will be processed.'}
              </div>

              {/* Order summary */}
              <div className="p-4 rounded-xl border border-border bg-card space-y-3">
                <h3 className="font-semibold text-foreground">{isFr ? 'Formation IA spéciale CRFPA' : 'Special CRFPA AI Training'}</h3>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline"><Clock className="w-3 h-3 mr-1" />{isFr ? '2 heures' : '2 hours'}</Badge>
                  <Badge variant="outline"><Monitor className="w-3 h-3 mr-1" />{isFr ? 'Visioconférence' : 'Video conference'}</Badge>
                </div>
                {selectedDate && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CalendarIcon className="w-4 h-4 text-primary" />
                    <span className="capitalize">{formattedSelectedDate}</span>
                    <span>• 12:15 – 14:15</span>
                  </div>
                )}
                {!user && guestName && (
                  <p className="text-sm text-muted-foreground">{guestName} — {guestEmail}</p>
                )}
                <div className="border-t border-border pt-3 flex justify-between items-center">
                  <span className="text-muted-foreground">{isFr ? 'Total HT' : 'Total excl. VAT'}</span>
                  <span className="text-2xl font-bold text-foreground">{CRFPA_PRICE}</span>
                </div>
              </div>

              {/* Card fields */}
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>{isFr ? 'Numéro de carte' : 'Card number'}</Label>
                  <Input placeholder="4242 4242 4242 4242" value={cardNumber} onChange={(e) => setCardNumber(e.target.value)} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>{isFr ? 'Expiration' : 'Expiry'}</Label>
                    <Input placeholder="MM/AA" value={cardExpiry} onChange={(e) => setCardExpiry(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>CVC</Label>
                    <Input placeholder="123" value={cardCvc} onChange={(e) => setCardCvc(e.target.value)} />
                  </div>
                </div>
              </div>

              <div className="flex justify-between pt-2">
                <Button variant="outline" onClick={() => setStep('slot')}>
                  <ArrowLeft className="w-4 h-4 mr-2" />{isFr ? 'Retour' : 'Back'}
                </Button>
                <Button onClick={handlePayment} disabled={isSubmitting} size="lg">
                  {isSubmitting
                    ? (isFr ? 'Traitement...' : 'Processing...')
                    : (isFr ? `Payer ${CRFPA_PRICE}` : `Pay ${CRFPA_PRICE}`)}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 5: Confirmation */}
        {step === 'confirmation' && (
          <Card className="border-border shadow-lg">
            <CardContent className="py-12 text-center space-y-6">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-2">
                  {isFr ? 'Réservation confirmée !' : 'Booking confirmed!'}
                </h2>
                <p className="text-muted-foreground max-w-md mx-auto">
                  {isFr
                    ? 'Un email de confirmation a été envoyé. Notre équipe vous contactera pour finaliser les détails de votre session.'
                    : 'A confirmation email has been sent. Our team will contact you to finalize your session details.'}
                </p>
                {selectedDate && (
                  <p className="text-primary font-semibold mt-3 capitalize">
                    {formattedSelectedDate} • 12:15 – 14:15
                  </p>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
                <Button asChild variant="outline">
                  <Link to={isFr ? '/formation-ia-avocat' : '/legal-ai-training-lawyers'}>
                    {isFr ? 'Retour aux formations' : 'Back to trainings'}
                  </Link>
                </Button>
                <Button asChild>
                  <Link to="/contact">
                    {isFr ? 'Nous contacter' : 'Contact us'}
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      <WaitlistDialog
        open={showWaitlist}
        onOpenChange={setShowWaitlist}
        trainingSlug="crfpa-ia"
        trainingTitle={isFr ? 'Formation IA spéciale CRFPA' : 'Special CRFPA AI Training'}
        initialEmail={user?.email || guestEmail || ''}
        selectedDate={selectedDate ? format(selectedDate, 'yyyy-MM-dd') : undefined}
      />
    </div>
  );
};
